import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {REDIRECT_TO_LEGACY_LOGIN} from 'app/core/helpers/redirect';
import {AuthService, CurrentUser} from 'app/core/services/auth.service';
import {DomService} from 'app/core/services/dom.service';
import {AppUrls} from 'app/core/url/app-urls.service';
import {MenuItemType} from 'app/shared/components/drop-down-menu/menu-item/menu-item.component';
import {BehaviorSubject, fromEvent, of, Subscription} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {DemoService} from 'app/core/services/demo.service';
import {ThemeCustomizationService} from 'app/core/services/theme-customization.service';
import {ErrorNotifierService} from 'app/core/error-handler/error-notifier.service';
import {ThemeMode} from 'src/globalTypes';
import {LayoutService} from 'app/core/services/layout.service';

@Component({
    selector: 'user-bar',
    templateUrl: './user-bar.component.html',
    styleUrls: ['./user-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserBarComponent implements OnInit, OnDestroy {
    @Input() user: CurrentUser;

    AppUrls = AppUrls;

    userBarVisible = false;
    MenuItemType = MenuItemType;
    private removeDocClickListener: Subscription = Subscription.EMPTY;

    showSomeUserBarIcons = true;
    sub: Subscription = Subscription.EMPTY;

    switchColorTheme: boolean;
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        public demo: DemoService,
        private domService: DomService,
        private layout: LayoutService,
        private cd: ChangeDetectorRef,
        private authService: AuthService,
        private router: Router,
        private elRef: ElementRef,
        @Inject(REDIRECT_TO_LEGACY_LOGIN) public redirectToHome: () => void,
        private themeCustom: ThemeCustomizationService,
        private errorHandler: ErrorNotifierService
    ) {}

    signOut() {
        this.authService.signOut().subscribe(() => this.redirectToHome());
    }

    ngOnInit() {
        if (!this.domService.isOnboardingShowCreateApp) {
            return;
        }

        this.sub = this.domService.isOnboardingShowCreateApp.subscribe(state => {
            this.showSomeUserBarIcons = !state;
            this.cd.detectChanges();
        });

        this.switchColorTheme = Boolean(this.user.themeMode === ThemeMode.DARK);
    }

    ngOnDestroy() {
        this.safeRemoveDocClickListener();
        this.sub.unsubscribe();
    }

    onToggleBtnClick() {
        this.userBarVisible = !this.userBarVisible;
        this.cd.markForCheck();
        if (this.userBarVisible) {
            this.removeDocClickListener = fromEvent(document, 'click', {capture: true})
                .pipe(
                    switchMap((e: MouseEvent) =>
                        this.elRef.nativeElement.contains(e.target) ? this.router.events : of(e).pipe(tap(() => e.stopPropagation()))
                    )
                )
                .subscribe(() => this.onDocClick());
        } else {
            this.safeRemoveDocClickListener();
        }
    }

    safeRemoveDocClickListener() {
        this.removeDocClickListener.unsubscribe();
    }

    onDocClick() {
        this.userBarVisible = false;
        this.safeRemoveDocClickListener();
        this.cd.markForCheck();
    }

    onValueChangesTheme(darkMode: any) {
        this.loading$.next(true);
        this.layout.reloadFrame$.next(true);
        this.themeCustom.setDarkThemeRubyPages(this.checkThemeType(Number(darkMode))).subscribe(
            mode => {
                console.debug('Mode of the theme has been sent to server!', mode);
                this.switchColorTheme = mode.setTheme;
                this.loading$.next(false);
                this.layout.reloadFrame$.next(false);
            },
            err => {
                console.error(err, 'Failed to send theme mode to server!');
                this.loading$.next(false);
                this.layout.reloadFrame$.next(false);
                this.errorHandler.notifyUser(err);
            }
        );
    }

    checkThemeType(mode: number): ThemeMode {
        switch (mode) {
            case 0:
                return ThemeMode.LIGHT;
            case 1:
                return ThemeMode.DARK;
        }
    }
}
